import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { FULL_MEMBER_ROLES, ADMIN_PLUS } from "../constants";
import { channelAccessFilter } from "./access-filters";

// Channel types that act like message-bearing channels. Used to keep
// type='space' rows out of generic channel queries (post migration 120
// spaces share the channels table).
const CHANNEL_LIKE_TYPES = ["channel", "dm", "group_dm"] as const;

// Per-channel message limit for the workspace-wide preload (see
// `byWorkspaceWithRecentMessages` below). Independent of the active
// channel's MESSAGE_PAGE_SIZE — the preload covers the visible
// viewport for "instant feel" on channel switch; the active query
// fills in deeper history + rich joins. Exported so the skeleton
// gate in `use-channel-message-list.ts` can match this threshold —
// otherwise we hide the preloaded rows behind the loading skeleton
// and the "instant feel" claim collapses.
export const PRELOAD_MESSAGES_PER_CHANNEL = 50;

// Channel-count cap for the workspace-wide preload. Defense against
// power-user / mega-workspace cases where the user has access to
// hundreds of channels — without this bound the preload payload is
// unbounded (e.g. 500 channels × 50 messages = 25K rows). Caps total
// preload at PRELOAD_CHANNEL_LIMIT × PRELOAD_MESSAGES_PER_CHANNEL =
// 200 × 50 = 10K rows worst-case. ZQL appends PK to ORDER BY
// automatically (see .claude/rules/zero-schema.md), so the 200
// returned are deterministic-by-id but NOT activity-ranked. Adding
// activity-based ordering would require a denormalized
// `last_message_at` column on `channels` — deferred.
const PRELOAD_CHANNEL_LIMIT = 200;

// Canonical "open a channel" subscription. Consolidates the prior
// `channels.byId` + `channel_members.byChannel` + `pins.byChannel`
// fan-out that MessageArea opened per active channel (multiplied by
// MULTIPLEX_CAP). Outer auth is `channelAccessFilter(sub)` — caller
// must be a direct, active member; the `pins` relation inherits that
// gate transitively (no separate where filter needed). No nested
// `.related("user")` because MessageArea consumers read user data
// from `renderUserMap` / `activeUserMap` selectors, not from joined
// member rows — eager user hydration would inflate the composite
// payload without a consumer.
const byIdComposite = defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
  zql.channels
    .where("id", id)
    .where(channelAccessFilter(sub))
    // Soft-delete: exclude kicked rows from the eagerly-loaded members
    // slice so member-rendering UIs (MessageArea header, mention picker,
    // sidebar) reflect the live roster.
    .related("members", (q) => q.where("removed_at", "IS", null))
    .related("pins")
    .one(),
);

// Pre-existing query — kept as-is to preserve the original shape
// (members WITH eager `.related("user")` joins, NO pins). Smoke tests
// at apps/desktop/src/lib/zero.test.ts and server/shared-contract.test.ts
// only assert existence + callability, but keeping the original AST
// avoids a silent shared-query contract change under a name that
// implies backwards compatibility. Drop in a follow-up cosmetic PR
// once we're certain no caller depends on `member.user` from this
// query name (current grep shows none).
const byIdWithMembers = defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
  zql.channels
    .where("id", id)
    .where(channelAccessFilter(sub))
    .related("members", (q) =>
      q.where("removed_at", "IS", null).related("user"),
    )
    .one(),
);

// Workspace-wide message preload — replaces the 15 per-channel
// `messages.byChannelComposite` preloads opened by usePreloadChannels
// with ONE subscription per active workspace. Saves up to 14
// always-on Zero pipelines per active user.
//
// Argument: workspaceId | null
//   • string  → workspace channels in that workspace + all DMs
//   • null    → DMs only (Home scope — no workspace selected)
// Each value gets its own query identity (Zero hashes (name, args)),
// so workspace switch closes the prior pipeline and opens a new one.
//
// Includes:
//   • Channels in the active workspace where caller is an active
//     (non-removed) member
//   • DMs / group_dms (workspace_id IS NULL) where caller is an
//     active member AND has not hidden the conversation
// Excludes: archived channels, thread replies (top-level only),
// spaces (type='space'), hidden DMs (matches the existing
// `dmsByUserWithMembers` semantics — users dismiss DMs by toggling
// `channel_members.hidden = true` and those should not be
// preloaded). For non-DM channels the `hidden` flag is always
// false, so the inline filter is functionally a no-op there.
//
// Sender joined eagerly so avatars are instant on channel switch.
// NO reactions / attachments / link_previews / calendar_attendees /
// email_participants / call_participants — those load via the
// active channel's `messages.byChannelComposite` when the user
// opens a channel. Different query identity from byChannelComposite,
// but the same per-channel sort order (created_at desc, id desc),
// so Zero's documented prefix guarantee
// (https://zero.rocicorp.dev/docs/queries#consistency) means the
// active query's first render serves whatever rows this preload
// populated locally.
//
// Auth: inline `whereExists("members", ...)` is stricter than
// `channelAccessFilter` — it adds `hidden = false` to the membership
// predicate. The preload's purpose is "show me everything I'd want
// instant access to" — explicitly hidden DMs are not in that set.
const byWorkspaceWithRecentMessages = defineQuery(
  z.string().nullable(),
  ({ args: workspaceId, ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .where("is_archived", false)
      .where(({ or, cmp }) =>
        workspaceId === null
          ? cmp("workspace_id", "IS", null)
          : or(
              cmp("workspace_id", workspaceId),
              cmp("workspace_id", "IS", null),
            ),
      )
      .whereExists("members", (mq) =>
        mq
          .where("user_id", sub)
          .where("removed_at", "IS", null)
          .where("hidden", false),
      )
      .related("messages", (q) =>
        q
          .where("thread_id", "IS", null)
          .orderBy("created_at", "desc")
          .orderBy("id", "desc")
          .limit(PRELOAD_MESSAGES_PER_CHANNEL)
          .related("sender")
          // Match the `.related()` chain on `byChannelComposite` so the
          // preload populates local SQLite with everything a cold channel
          // open needs to render — reactions, attachments, link previews,
          // mentions junction. Without these, the active channel query
          // pulls them on-demand and they "pop in" after first paint
          // (Zero syncs the union of active queries; preload covered
          // messages but not their child relations).
          //
          // The 7 wider relations live below `messages.byChannelComposite`
          // (reactions/attachments/link_previews/message_mentions for
          // top-level rendering; the calendar/email/call participants are
          // intentionally NOT included here — they're side-table joins on
          // event-style messages and rare enough that lazy fetch is fine).
          .related("reactions", (rq) => rq.related("user"))
          .related("attachments")
          .related("link_previews")
          .related("message_mentions"),
      )
      .limit(PRELOAD_CHANNEL_LIMIT),
);

export const channelsQueries = {
  // Soft-delete: caller-side `user_id = sub` checks MUST also filter
  // `removed_at IS NULL` so a kicked user immediately loses visibility
  // (see `.claude/rules/queries.md` and the same fix on
  // `workspaces.byUser`). Without it, a kicked user keeps seeing
  // channels in their sidebar until they reload because the
  // soft-deleted `channel_members` row still satisfies
  // `user_id = sub`.
  byUser: defineQuery(({ ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .whereExists("members", (q) =>
        q.where("user_id", sub).where("removed_at", "IS", null),
      ),
  ),
  activeByUser: defineQuery(({ ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .where("is_archived", false)
      .whereExists("members", (q) =>
        q.where("user_id", sub).where("removed_at", "IS", null),
      ),
  ),
  activeByUserWithPreview: defineQuery(({ ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .where("is_archived", false)
      .whereExists("members", (q) =>
        q.where("user_id", sub).where("removed_at", "IS", null),
      )
      .related("messages", (q) =>
        q
          .where("thread_id", "IS", null)
          // Redundant today (channel membership already enforced above) but
          // future-proofs against per-message permission models.
          .whereExists("channel", (cq) =>
            cq.whereExists("members", (mq) =>
              mq.where("user_id", sub).where("removed_at", "IS", null),
            ),
          )
          .orderBy("created_at", "desc")
          .limit(1),
      ),
  ),
  dmsByUser: defineQuery(({ ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", ["dm", "group_dm"])
      .where("is_archived", false)
      .whereExists("members", (q) =>
        q.where("user_id", sub).where("removed_at", "IS", null),
      ),
  ),
  // `includeHidden` (default false) lets the sidebar opt into seeing the
  // caller's own hidden DMs so it can filter/sort them client-side (hidden
  // rows resurface while unread). Every other caller omits the arg and keeps
  // the hidden-stripping behavior. See `use-dm-list-data.ts`.
  dmsByUserWithMembers: defineQuery(
    z.boolean().optional(),
    ({ args: includeHidden, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", ["dm", "group_dm"])
        .where("is_archived", false)
        .whereExists("members", (q) => {
          let mine = q.where("user_id", sub).where("removed_at", "IS", null);
          if (!includeHidden) mine = mine.where("hidden", false);
          return mine;
        })
        .related("members", (q) =>
          q.where("removed_at", "IS", null).related("user"),
        ),
  ),
  // Workspace-scoped DMs: DMs where I'm a member AND at least one other
  // member is in this workspace. DMs have workspace_id IS NULL (they're
  // universal), so we join through channel_members → users → workspace_members.
  dmsByWorkspaceAndUserWithMembers: defineQuery(
    z.object({
      workspaceId: z.string(),
      includeHidden: z.boolean().optional(),
    }),
    ({ args: { workspaceId, includeHidden }, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", ["dm", "group_dm"])
        .where("is_archived", false)
        .whereExists("members", (q) => {
          let mine = q.where("user_id", sub).where("removed_at", "IS", null);
          if (!includeHidden) mine = mine.where("hidden", false);
          return mine;
        })
        .whereExists("members", (q) =>
          q
            .where("user_id", "!=", sub)
            .whereExists("user", (uq) =>
              uq.whereExists("workspace_memberships", (wmq) =>
                wmq
                  .where("workspace_id", workspaceId)
                  .where("removed_at", "IS", null),
              ),
            ),
        )
        .related("members", (q) =>
          q.where("removed_at", "IS", null).related("user"),
        ),
  ),
  byWorkspace: defineQuery(z.string(), ({ args: wsId, ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .where("workspace_id", wsId)
      .where(channelAccessFilter(sub)),
  ),
  // Admin view: all channels in workspace (public + private), regardless of
  // membership, with `members` eagerly loaded for count display. Gated on
  // ADMIN_PLUS. Used by AdminChannelsSpacesTab.
  byWorkspaceForAdminWithMembers: defineQuery(
    z.string(),
    ({ args: wsId, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
        .where("workspace_id", wsId)
        .whereExists("workspace", (wq) =>
          wq.whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("removed_at", "IS", null)
              .where("role", "IN", ADMIN_PLUS),
          ),
        )
        .related("members", (q) => q.where("removed_at", "IS", null)),
  ),
  byWorkspaceAndUser: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
        .where("workspace_id", workspaceId)
        .whereExists("members", (q) =>
          q.where("user_id", sub).where("removed_at", "IS", null),
        ),
  ),
  byWorkspaceAndUserWithMembers: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
        .where("workspace_id", workspaceId)
        .whereExists("members", (q) =>
          q.where("user_id", sub).where("removed_at", "IS", null),
        )
        .related("members", (q) => q.where("removed_at", "IS", null)),
  ),
  publicByWorkspace: defineQuery(z.string(), ({ args: wsId, ctx: { sub } }) =>
    zql.channels
      .where("workspace_id", wsId)
      .where("type", "channel")
      .where(channelAccessFilter(sub)),
  ),
  // Browse Channels: all public channels in workspace, regardless of membership.
  // Used for channel discovery — users can join from here. Private channels
  // are excluded (only accessible via invite). Auth: must be a full workspace member.
  publicByWorkspaceForBrowse: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
        .where("workspace_id", workspaceId)
        .where("is_private", false)
        .where("is_archived", false)
        .whereExists("workspace", (wq) =>
          wq.whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("removed_at", "IS", null)
              .where("role", "IN", FULL_MEMBER_ROLES),
          ),
        ),
  ),
  // byId / byIdComposite / byIdWithMembers are intentionally type-agnostic —
  // by-id lookups are used by callers that already know which row they
  // want. Callers that pass a space ID get a space row back (and that's
  // their problem to filter). Spaces should be looked up via
  // spacesQueries.byId instead.
  byId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.channels.where("id", id).where(channelAccessFilter(sub)).one(),
  ),
  // Composite (members + pins) for MessageArea. See top-of-file docblock.
  byIdComposite,
  // Pre-existing query — eager .related("members", q.related("user")),
  // no pins. Distinct from byIdComposite. See top-of-file docblock.
  byIdWithMembers,
  // Workspace-wide message preload (channels + DMs, recent messages
  // + sender). Replaces 15 per-channel byChannelComposite preloads.
  // See top-of-file docblock.
  byWorkspaceWithRecentMessages,
  all: defineQuery(({ ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .whereExists("members", (q) =>
        q.where("user_id", sub).where("removed_at", "IS", null),
      ),
  ),
  active: defineQuery(z.string(), ({ args: wsId, ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .where("workspace_id", wsId)
      .where("is_archived", false)
      .where(channelAccessFilter(sub)),
  ),
  archived: defineQuery(z.string(), ({ args: wsId, ctx: { sub } }) =>
    zql.channels
      .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
      .where("workspace_id", wsId)
      .where("is_archived", true)
      .where(channelAccessFilter(sub)),
  ),
  byType: defineQuery(
    z.object({ workspaceId: z.string(), type: z.string() }),
    ({ args: { workspaceId, type }, ctx: { sub } }) =>
      zql.channels
        .where("type", "IN", CHANNEL_LIKE_TYPES as readonly string[])
        .where("workspace_id", workspaceId)
        .where("type", type)
        .where(channelAccessFilter(sub)),
  ),
};
